import React, { useState, useEffect } from 'react';
import CompletionAgentExecution from './CompletionAgentExecution';

function ItemForm({ inventory, item, selectedItemType, availableItemTypes, onSave, onClose, onItemTypeChange }) {
  const [fieldValues, setFieldValues] = useState({});
  const [currentItemType, setCurrentItemType] = useState(null);

  // Initialize currentItemType on component mount or when editing a different item
  useEffect(() => {
    let initialItemType = null;
    
    if (item && item.item_type_id && availableItemTypes) {
      // For editing: find the item type based on the item's item_type_id
      initialItemType = availableItemTypes.find(type => 
        type.id == item.item_type_id || type.id === parseInt(item.item_type_id)
      );
    }
    
    if (!initialItemType) {
      // Fallback to selectedItemType (from modal selection) or default
      initialItemType = selectedItemType || inventory.default_item_type;
    }
    
    setCurrentItemType(initialItemType);
  }, [item?.id, selectedItemType, availableItemTypes, inventory.default_item_type]);

  useEffect(() => {
    // Use currentItemType fields if available, otherwise fall back to inventory fields
    const fieldsToUse = currentItemType?.fields || inventory.fields || [];
    
    // Always initialize ALL fields defined in the item type
    const initialValues = {};
    fieldsToUse.forEach(field => {
      initialValues[field.name] = '';
    });
    // Add location field if inventory has location type
    if (inventory.locations && inventory.locations.length > 0) {
      initialValues['Location'] = '';
    }
    
    // If editing an existing item, overlay the existing values
    if (item && item.field_values) {
      Object.keys(item.field_values).forEach(fieldName => {
        // Only set if field exists in current item type
        if (initialValues.hasOwnProperty(fieldName)) {
          initialValues[fieldName] = item.field_values[fieldName];
        }
      });
    }
    
    setFieldValues(initialValues);
  }, [item, currentItemType, inventory.fields, inventory.locations]);

  const handleFieldChange = (fieldName, value) => {
    setFieldValues(prevValues => ({
      ...prevValues,
      [fieldName]: value
    }));
  };

  const handleItemTypeChange = (newItemType) => {
    setCurrentItemType(newItemType);
    if (onItemTypeChange) {
      onItemTypeChange(newItemType);
    }
    
    // When changing item type, preserve values for fields that exist in both types
    const newValues = {};
    const newFieldNames = new Set(newItemType.fields.map(field => field.name));
    
    // Initialize all new fields
    newItemType.fields.forEach(field => {
      // Preserve existing value if field exists in both old and new type
      if (newFieldNames.has(field.name) && fieldValues[field.name] !== undefined) {
        newValues[field.name] = fieldValues[field.name];
      } else {
        newValues[field.name] = '';
      }
    });
    
    // Add location field if inventory has location type
    if (inventory.locations && inventory.locations.length > 0) {
      newValues['Location'] = fieldValues['Location'] || '';
    }
    
    setFieldValues(newValues);
  };

  // Find the first non-empty field value to use as search value for completion agents
  const getSearchFieldValue = () => {
    // Look for common search fields first
    const preferredFields = ['Title', 'Name', 'Product Name', 'Item Name'];
    for (const fieldName of preferredFields) {
      if (fieldValues[fieldName] && fieldValues[fieldName].trim()) {
        return fieldValues[fieldName].trim();
      }
    }
    
    // Fall back to any non-empty field
    for (const [fieldName, value] of Object.entries(fieldValues)) {
      if (value && typeof value === 'string' && value.trim()) {
        return value.trim();
      }
    }
    
    return '';
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate required fields
    const fieldsToUse = currentItemType?.fields || inventory.fields || [];
    const missingRequired = fieldsToUse
      .filter(field => {
        if (!field.required) return false;
        
        const value = fieldValues[field.name];
        
        // For checkbox fields, check if at least one option is selected
        if (field.type === 'checkbox') {
          return !Array.isArray(value) || value.length === 0;
        }
        
        // For other fields, check if value exists and is not empty
        return !value || (typeof value === 'string' && !value.trim());
      })
      .map(field => field.name);

    // Check if location is required (when inventory has location type but no location selected)
    if (inventory.locations && inventory.locations.length > 0 && !fieldValues['Location']?.trim()) {
      missingRequired.push('Location');
    }

    if (missingRequired.length > 0) {
      alert(`Please fill in required fields: ${missingRequired.join(', ')}`);
      return;
    }

    const itemTypeId = currentItemType?.id || inventory.default_item_type?.id || inventory.inventory_type_id;
    
    onSave({ 
      field_values: fieldValues,
      item_type_id: itemTypeId
    });
  };

  const renderFieldInput = (field) => {
    const value = fieldValues[field.name] || '';

    switch (field.type) {
      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            required={field.required}
            rows="3"
          />
        );
      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            required={field.required}
          />
        );
      case 'date':
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            required={field.required}
          />
        );
      case 'select':
        return (
          <select
            value={value}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            required={field.required}
          >
            <option value="">-- Select an option --</option>
            {field.options && field.options.map((option, index) => (
              <option key={index} value={option}>
                {option}
              </option>
            ))}
          </select>
        );
      case 'radio':
        return (
          <div>
            {field.options && field.options.map((option, index) => (
              <label key={index} style={{ display: 'block', marginBottom: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name={field.name}
                  value={option}
                  checked={value === option}
                  onChange={(e) => handleFieldChange(field.name, e.target.value)}
                  required={field.required}
                  style={{ marginRight: '0.5rem', width: 'auto' }}
                />
                {option}
              </label>
            ))}
          </div>
        );
      case 'checkbox':
        const checkboxValues = Array.isArray(value) ? value : (value ? [value] : []);
        return (
          <div>
            {field.options && field.options.map((option, index) => (
              <label key={index} style={{ display: 'block', marginBottom: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  value={option}
                  checked={checkboxValues.includes(option)}
                  onChange={(e) => {
                    const newValues = e.target.checked
                      ? [...checkboxValues, option]
                      : checkboxValues.filter(v => v !== option);
                    handleFieldChange(field.name, newValues);
                  }}
                  style={{ marginRight: '0.5rem', width: 'auto' }}
                />
                {option}
              </label>
            ))}
            {field.required && checkboxValues.length === 0 && (
              <p style={{ fontSize: '0.8rem', color: '#e74c3c', margin: '0.5rem 0 0 0' }}>
                Please select at least one option
              </p>
            )}
          </div>
        );
      default:
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            required={field.required}
          />
        );
    }
  };

  // Don't render until currentItemType is set
  if (!currentItemType) {
    return (
      <div className="modal">
        <div className="modal-content">
          <div>Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal">
      <div className="modal-content">
        <h2>
          {item ? 'Edit Item' : 'Add New Item'}
          <span style={{ fontSize: '0.8rem', color: '#666', fontWeight: 'normal', marginLeft: '0.5rem' }}>
            ({currentItemType.name})
          </span>
        </h2>

        {availableItemTypes && availableItemTypes.length > 1 && (
          <div className="form-group" style={{ marginBottom: '2rem', border: '1px solid #ddd', padding: '1rem', borderRadius: '4px', backgroundColor: '#f9f9f9' }}>
            <label>Item Type *</label>
            <select 
              value={currentItemType?.id || ''} 
              onChange={(e) => {
                const targetValue = e.target.value;
                const newItemType = availableItemTypes.find(type => 
                  type.id == targetValue || type.id === parseInt(targetValue)
                );
                if (newItemType) {
                  handleItemTypeChange(newItemType);
                }
              }}
              style={{ marginBottom: '0.5rem' }}
            >
              {availableItemTypes.map(itemType => (
                <option key={itemType.id} value={itemType.id}>
                  {itemType.name} {itemType.is_default ? '(Default)' : ''}
                </option>
              ))}
            </select>
            {currentItemType && (
              <div style={{ fontSize: '0.8rem', color: '#666' }}>
                {currentItemType.description && (
                  <div style={{ marginBottom: '0.25rem' }}>{currentItemType.description}</div>
                )}
                <div>{currentItemType.fields?.length || 0} fields in this item type</div>
              </div>
            )}
          </div>
        )}
        
        <CompletionAgentExecution
          fieldValues={fieldValues}
          onFieldUpdate={handleFieldChange}
          inventoryTypeId={currentItemType?.id || inventory.inventory_type_id}
        />
        
        <form onSubmit={handleSubmit}>
          {(currentItemType?.fields || inventory.fields || []).map((field, index) => (
            <div key={index} className="form-group">
              <label>
                {field.name}
                {field.required && <span style={{ color: '#e74c3c' }}> *</span>}
              </label>
              {field.description && (
                <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.5rem' }}>
                  {field.description}
                </p>
              )}
              {renderFieldInput(field)}
            </div>
          ))}

          {/* Add location field if inventory has location type */}
          {inventory.locations && inventory.locations.length > 0 && (
            <div className="form-group">
              <label>
                Location
                <span style={{ color: '#e74c3c' }}> *</span>
              </label>
              <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.5rem' }}>
                Select the location where this item is stored
              </p>
              <select
                value={fieldValues['Location'] || ''}
                onChange={(e) => handleFieldChange('Location', e.target.value)}
                required
              >
                <option value="">-- Select a location --</option>
                {inventory.locations.map((location, index) => (
                  <option key={index} value={location}>
                    {location}
                  </option>
                ))}
                <option value="unknown">unknown</option>
              </select>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '2rem' }}>
            <button type="button" className="btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn">
              {item ? 'Update Item' : 'Add Item'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ItemForm;